/* eslint-disable no-console */
import { useEffect, useRef } from 'react';
import type * as Ably from 'ably';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import {
  ablySpaceChannelFor,
  ensureResource,
  releaseResource
} from 'lib/realtime/AblyClient';

const EVENT_NAME = 'rfq.files_changed';

/**
 * Reacts to another user uploading files to this RFQ. When the uploader
 * finishes, they broadcast `rfq.files_changed`; this hook re-runs the file
 * fetch (`onFilesChanged` → orchestrator `fetchFiles`) so the viewer picks up
 * the new files and starts the same AblyManager streaming — items then appear
 * item-by-item instead of only after a manual refresh.
 *
 * Rides the Spaces channel (browsers can publish there). Self-echo is filtered
 * by `actor.userId` so the uploader doesn't double-fetch their own signal.
 */
export const useRfqFilesRealtime = (
  rfqId: string | undefined,
  onFilesChanged: () => void
) => {
  const { user } = useAppContext();
  // Hold the latest callback so a changing identity doesn't re-subscribe.
  const cbRef = useRef(onFilesChanged);
  cbRef.current = onFilesChanged;

  useEffect(() => {
    if (!rfqId) return undefined;
    let cancelled = false;
    let channel: Ably.RealtimeChannel | null = null;
    let listener: ((m: Ably.Message) => void) | null = null;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const currentUserId = user?.ID ? String(user.ID) : null;

    (async () => {
      try {
        const ably = await ensureResource({ type: 'rfq', id: rfqId });
        if (cancelled) return;
        channel = ably.channels.get(
          ablySpaceChannelFor({ type: 'rfq', id: rfqId })
        );
        listener = (msg: Ably.Message) => {
          const data = msg?.data as { actor?: { userId?: string } } | undefined;
          // Skip our own upload echo — we already fetched after uploading.
          if (data?.actor?.userId && data.actor.userId === currentUserId) {
            return;
          }
          console.info(
            '[rfq-files] files_changed — refetching to stream new items'
          );
          // Small debounce so multiple file uploads collapse into one fetch and
          // the new file's status is queryable by the time we re-fetch.
          if (debounce) clearTimeout(debounce);
          debounce = setTimeout(() => cbRef.current(), 800);
        };
        await channel.subscribe(EVENT_NAME, listener);
      } catch (err) {
        console.warn('useRfqFilesRealtime: subscribe failed', err);
      }
    })();

    return () => {
      cancelled = true;
      if (debounce) clearTimeout(debounce);
      if (channel && listener) {
        try {
          channel.unsubscribe(EVENT_NAME, listener);
        } catch (err) {
          console.warn('useRfqFilesRealtime: unsubscribe failed', err);
        }
      }
      releaseResource({ type: 'rfq', id: rfqId }).catch(err =>
        console.warn('useRfqFilesRealtime: release failed', err)
      );
    };
  }, [rfqId, user?.ID]);
};
